import { Box, CircularProgress, Modal, Paper, Typography } from "@mui/material"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { sleep } from "src/app/utils"
import {
  useAutoApplyDiscountCodeMutation,
  useLazyGetCheckoutQuery,
  useLazyGetVolumeDiscountProductsByIDsQuery,
} from "src/features/checkout/storefrontApi"
import { useCartDiscountCodesUpdateMutation, useLazyGetCartQuery } from "src/features/checkout/storefrontCartApi"

export default function AutoApplyVolumeDiscountsModal() {
  const [volumeDiscounts, setVolumeDiscounts] = useState([])
  const [openModal, setOpenModal] = useState(false)
  const config = useSelector((state) => state.checkout.config)
  const {cart, checkout} = useSelector((state) => state.checkout)

  const [getVolumeDiscountProducts, { data: getVolumeDiscountProductsData }] =
    useLazyGetVolumeDiscountProductsByIDsQuery()

  const [applyDiscountCode] = useAutoApplyDiscountCodeMutation()

  const [
    cartDiscountCodesUpdate,
    {
      isLoading: cartDiscountCodesUpdateLoading,
      data: cartDiscountCodesUpdateData,
      error: cartDiscountCodesUpdateError,
    }
  ] = useCartDiscountCodesUpdateMutation()

  const [getCheckout] = useLazyGetCheckoutQuery()
  const [getCart] = useLazyGetCartQuery()

  async function autoApplyDiscountCode(discountCode, retry = false) {
    if (retry) {
      console.info(
        new Date(),
        `Retrying discount code application ${discountCode}...`,
      )
    }

    try {
      const response = await applyDiscountCode({
        checkoutId: checkout.id,
        discountCode,
      }).unwrap()

      if (response.checkoutDiscountCodeApplyV2) {
        if (response.checkoutDiscountCodeApplyV2.checkoutUserErrors.length) {
          console.info(
            new Date(),
            `An error occured while trying to apply volume discount ${discountCode}, errors: ${response.checkoutDiscountCodeApplyV2.checkoutUserErrors
              .map((error) => error.code)
              .join(",")}`,
          )
        } else {
          console.info(new Date(), `Applied discount code: ${discountCode}`)
        }
      }
    } catch (error) {
      // Retry discount code application if network request failed or if we have been rate limited.
      if (
        error?.data?.message.includes("Too many requests.") ||
        error?.data?.message.includes("Network request failed")
      ) {
        await sleep(1000)
        await autoApplyDiscountCode(discountCode, true)
      }
    }
  }

  async function autoApplyDiscountCodes() {
    const appliedDiscountCodes = cart.discountCodes.filter(dc => dc.applicable).map(
      (appliedDiscountCode) => appliedDiscountCode.code,
    )
    let discountCodeApplied = false
    let discountCodesToApply = []

    for (const lineItem of cart.lines.edges) {
      const variantId = lineItem.node.merchandise?.id
      if (!variantId) continue // Variant id is null if the product is inactive/draft status.

      // Buffalo Trace Kentucky Straight Bourbon Whiskey (1,000ml) Promo
      const customDiscountCode = 'BUFFALO_TRACE'
      const customDiscountVariantId = 'gid://shopify/ProductVariant/44397592543449'
      if (variantId === customDiscountVariantId &&
        parseFloat(cart.cost?.subtotalAmount?.amount || 0) > 200 &&
        !appliedDiscountCodes.includes(customDiscountCode)) {
        discountCodesToApply.push(customDiscountCode)
      }

      const productVolumeDiscounts = volumeDiscounts.find(
        (vd) => vd.variantIds && vd.variantIds.includes(variantId),
      )

      if (!productVolumeDiscounts) continue

      const discountCodeToApply = [...productVolumeDiscounts?.volume_discounts]
        .sort((a, b) => b.minimum_quantity - a.minimum_quantity)
        .find((c) => c.minimum_quantity <= lineItem.node.quantity)?.title

      if (discountCodeToApply) {
        if (appliedDiscountCodes.includes(discountCodeToApply)) {
          console.info(
            new Date(),
            `Volume discount ${discountCodeToApply} is already applied.`,
          )
        } else {
          setOpenModal(true)

          console.info(
            new Date(),
            `Attempting to apply discount code: ${discountCodeToApply}`,
          )

          discountCodesToApply.push(discountCodeToApply)
        }
      }
    }

    if (discountCodesToApply.length > 0) {
      let appliedDiscountCodes = cart.discountCodes?.filter(dc => dc.applicable).map(dc => dc.code) || [];
      await cartDiscountCodesUpdate({
        cartId: cart.id,
        discountCodes: [...appliedDiscountCodes, ...discountCodesToApply],
      })
    }

    setOpenModal(false)
  }

  async function autoApplyCustomDiscountCodes() {
    // Buffalo Trace Kentucky Straight Bourbon Whiskey (1,000ml) Promo
    const customDiscountCode = 'BUFFALO_TRACE'
    const customDiscountVariantId = 'gid://shopify/ProductVariant/44397592543449'
    const appliedDiscountCodes = cart.discountCodes.filter(dc => dc.applicable).map(
      (appliedDiscountCode) => appliedDiscountCode.code,
    )

    for (const lineItem of cart.lines.edges) {
      const variantId = lineItem.node.merchandise?.id
      if (!variantId) continue // Variant id is null if the product is inactive/draft status.

      if (variantId === customDiscountVariantId &&
        parseFloat(cart.cost.totalAmount?.amount || 0) > 200 &&
        !appliedDiscountCodes.includes(customDiscountCode)) {
        await autoApplyDiscountCode(customDiscountCode)
        await getCart(cart.id, false)
      }
    }
  }

  useEffect(() => {
    if (!config.volumeDiscountProducts.length) return

    getVolumeDiscountProducts(
      config.volumeDiscountProducts.map((item) => item.product_id),
    )
  }, [config])

  useEffect(() => {
    if (!getVolumeDiscountProductsData?.nodes?.length) return

    setVolumeDiscounts([
      ...config.volumeDiscountProducts.map((vd) => {
        const temp = { ...vd }
        try {
          temp.variantIds = [
            ...getVolumeDiscountProductsData.nodes
              .find((vdp) => vdp?.id === vd.product_id)
              ?.variants.edges.map((x) => x.node.id),
          ].flat()
        } catch (e) {
          temp.variantIds = []
        }
        return temp
      }),
    ])
  }, [getVolumeDiscountProductsData])

  useEffect(() => {
    if (!cart?.id) return
    autoApplyCustomDiscountCodes()
  }, [cart?.id])

  useEffect(() => {
    if (!cart?.id || !volumeDiscounts.length) return
    autoApplyDiscountCodes()
  }, [cart?.id, volumeDiscounts])

  useEffect(() => {
    // Close the modal even if discount code application is not yet done after 10s timeout.
    if (openModal) {
      setTimeout(() => {
        setOpenModal(false)
        getCart(cart.id, false)
      }, 10000)
    } else if (getVolumeDiscountProductsData) {
      getCart(cart.id, false)
    }
  }, [openModal])

  if (!openModal) return

  return (
    <Modal
      open={openModal}
      onClose={() => setOpenModal(false) && getCheckout(cart.id, false)}
      disableAutoFocus
      disableEnforceFocus
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        component={Paper}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        maxWidth="500px"
        p={4}
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1" textAlign="center">
          Volume discounts are being applied...
          <br />
          Please wait.
        </Typography>
      </Box>
    </Modal>
  )
}
